// pages/sections/reviewUtil.js
// Helpers shared by the Fall 2026 job/fee review sections (Nonstandard Job Names,
// Stale Jobs / Late Filings, Client Quoted Fee Review) — formatting, the API error
// contract and the admin Unmark call. Their endpoints answer errors as JSON
// `{ error }` (api/shared/httpJson.js).

// A Practice Engine date. PE stores Central wall-clock times and the SQL driver hands them over
// labelled UTC (date-only fields at midnight), so format in UTC: local time would show the day
// before. Also right for a plain 'YYYY-MM-DD'.
export function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { timeZone: 'UTC' });
}

// Hours to the nearest hundredth (1,234.50).
export function fmtHours(h) {
  const n = Number(h);
  return (Number.isFinite(n) ? n : 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// "Reviewed by x on <date>" — the tooltip on a completed row. `verb` is Reviewed / Submitted / …
export function doneTip({ by, at }, verb = 'Reviewed') {
  return `${verb}${by ? ` by ${by}` : ''}${at ? ` on ${new Date(at).toLocaleString()}` : ''}`;
}

// The order every review list uses: open rows first, completed rows last, each group by ClientCode ascending.
// Derive `isDone` from the rendered (optimistically patched) row, so a row sinks the moment it's completed.
// Returns a new array (never sorts `rows` in place — they may be shared state).
export function sortDoneLast(rows, isDone, code = r => r.clientCode) {
  return sortByTier(rows, r => (isDone(r) ? 1 : 0), code);
}

// Same, with more than two tiers: `tierOf(row)` is a number, lowest first; ties by ClientCode ascending.
export function sortByTier(rows, tierOf, code = r => r.clientCode) {
  return [...rows].sort((a, b) =>
    tierOf(a) - tierOf(b) ||
    String(code(a) || '').localeCompare(String(code(b) || ''), undefined, { numeric: true }));
}

// The message of a failed response: the JSON `error`, else the raw text, else the status.
export async function readError(res) {
  const text = await res.text().catch(() => '');
  try {
    const msg = JSON.parse(text).error;
    if (msg) return String(msg);
  } catch { /* not JSON */ }
  return text || `HTTP ${res.status}`;
}

// POST JSON to a review endpoint; throws Error(message) on a non-2xx answer.
export async function postJson(url, payload) {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await readError(res));
  return res;
}

// Admin Unmark: removes the row's signoff so it reads "not done" again.
export function postAdminUnmark(period, sectionKey, recordKey, role) {
  return postJson(`/api/diAdminUnmark/${period}/${sectionKey}`, { recordKey: String(recordKey), role });
}

// Optimistic row patcher over `useSectionData`'s setData. Returns the previous row (for revert).
export function patchRowIn(setData, jobIdx, updater) {
  let prevRow = null;
  setData(prev => {
    if (!prev) return prev;
    return {
      ...prev,
      rows: (prev.rows || []).map(r => {
        if (String(r.jobIdx) !== String(jobIdx)) return r;
        prevRow = r;
        return updater(r);
      }),
    };
  });
  return prevRow;
}
